import { useEffect, useState } from 'react';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import type { INzaServiceDeclarationActivity } from '../../interfaces/nzaServiceDeclarationActivity';
import {
    AdminTable,
    Currency,
    DeleteButton,
    EditButton,
    Header,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface INzaServiceDeclarationActivityListProps {
    nzaServiceDeclarationRuleId: string;
    serviceId?: string;
}

export function NzaServiceDeclarationActivityList(props: INzaServiceDeclarationActivityListProps) {
    const language = getSelectedLanguage();

    const [nzaServiceDeclarationActivities, setNzaServiceDeclarationActivities] = useState<
        INzaServiceDeclarationActivity[]
    >([]);
    const [isSearching, setIsSearching] = useState(true);
    const [total, setTotal] = useState(0);
    const [authorization, setAuthorization] = useState({
        canAdd: false,
    });
    const [page, setPage] = useState(1);

    useEffect(() => {
        search();
    }, []);

    let actionButtons: JSX.Element[] | undefined;
    if (authorization.canAdd) {
        actionButtons = [
            <LinkButton
                icon="add"
                key="addDeclarationActivity"
                to={getUrlAddEdit()}
            >
                {language.serviceDeclarationRules.addDeclarationActivity}
            </LinkButton>,
        ];
    }

    return (
        <>
            <Header
                actionButtons={actionButtons}
                content={language.serviceDeclarationRules.declarationActivities}
                smallHeader
            />

            <Loading
                item={nzaServiceDeclarationActivities}
                loaded={(loadedNzaServiceDeclarationActivities) => (
                    <>
                        <AdminTable>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{language.common.startDate}</TableHeader>
                                    <TableHeader>{language.common.endDate}</TableHeader>
                                    <TableHeader>{language.serviceDeclarationActivities.codeActiviteit}</TableHeader>
                                    <TableHeader>{language.serviceDeclarationActivities.activityType}</TableHeader>
                                    <TableHeader>
                                        {language.serviceDeclarationRules.supplementPriceActivity}
                                    </TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loadedNzaServiceDeclarationActivities.map((nzaServiceDeclarationActivity) => (
                                    <TableRow key={nzaServiceDeclarationActivity.id}>
                                        <TableData>
                                            <NullableFormattedDate value={nzaServiceDeclarationActivity.startDate} />
                                        </TableData>
                                        <TableData>
                                            <NullableFormattedDate value={nzaServiceDeclarationActivity.endDate} />
                                        </TableData>
                                        <TableData>
                                            {nzaServiceDeclarationActivity.serviceDeclarationActivity.codeActiviteit}
                                        </TableData>
                                        <TableData>
                                            {nzaServiceDeclarationActivity.serviceDeclarationActivity.activityType}
                                        </TableData>
                                        <TableData>
                                            <Currency value={nzaServiceDeclarationActivity.supplementPriceActivity} />
                                        </TableData>
                                        {nzaServiceDeclarationActivity.authorization.canUpdate && (
                                            <TableData align="right">
                                                <EditButton to={getUrlAddEdit(nzaServiceDeclarationActivity.id)} />
                                                {nzaServiceDeclarationActivity.authorization.canDelete && (
                                                    <DeleteButton
                                                        onClick={() => remove(nzaServiceDeclarationActivity.id)}
                                                    />
                                                )}
                                            </TableData>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </AdminTable>
                        <ListPager
                            isDisabled={isSearching}
                            onChangePage={handleNavigation}
                            page={page}
                            total={total}
                        />
                    </>
                )}
            />
        </>
    );

    function getUrlAddEdit(nzaServiceDeclarationActivityId?: string) {
        let url = '';

        if (nzaServiceDeclarationActivityId) {
            url = `/nzaServiceDeclarationActivities/${nzaServiceDeclarationActivityId}/edit`;
        } else {
            url = '/nzaServiceDeclarationActivities/add';
        }

        url += `?nzaServiceDeclarationRuleId=${props.nzaServiceDeclarationRuleId}`;

        if (props.serviceId) {
            url += '&serviceId=' + props.serviceId;
        }

        return url;
    }

    async function search() {
        const url = `/api/nzaServiceDeclarationActivities?nzaServiceDeclarationRulesId=${props.nzaServiceDeclarationRuleId}&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${page}`;
        setIsSearching(true);
        const result = await fetchJson(url, 'GET');
        setNzaServiceDeclarationActivities(result.json.items);
        setTotal(result.json.total);
        setAuthorization(result.json.authorization);
        setIsSearching(false);
    }

    async function handleNavigation(pageNumber: number) {
        setPage(pageNumber);
        await search();
    }

    async function remove(id: string) {
        const url = `/api/nzaServiceDeclarationActivities/${id}`;
        setIsSearching(true);
        await fetchJson(url, 'DELETE');
        await search();
    }
}
